import dayjs from 'dayjs';
import { notificationMatrixSource } from '../notificationMatrix';

// Shared display vocabulary for every notification surface — the bell
// popover (N1), the vendor tray page (N2/M1), the host tray (H1) and the
// host notifications page (H2) all read the same maps so a kind looks
// identical wherever it lands.

// Absolute stamps, frame style: 9:26 AM today, then Yesterday, then Tue,
// then Aug 5. (Ratified behavior — the frames' calendar-date drift is
// flagged to design.)
export function stampFor(createdAt) {
  const d = dayjs(createdAt);
  if (d.isSame(dayjs(), 'day')) return d.format('h:mm A');
  if (d.isSame(dayjs().subtract(1, 'day'), 'day')) return 'Yesterday';
  if (d.isAfter(dayjs().subtract(6, 'day').startOf('day'))) return d.format('ddd');
  return d.format('MMM D');
}

// kind → icon + tint tile, straight off the N-series rows.
const KIND_VISUALS = {
  document_needs_fix: { icon: 'shield-x', tile: 'red' },
  permit_expiring: { icon: 'calendar-x', tile: 'amber' },
  instagram_disconnected: { icon: 'brand-instagram', tile: 'red' },
  payout_sent: { icon: 'cash', tile: 'green' },
  sales_report_due: { icon: 'clipboard-list', tile: 'red' },
  sales_report_submitted: { icon: 'clipboard-list', tile: 'green' },
  sales_report_locked: { icon: 'clipboard-list', tile: 'amber' },
  cancellation_settled: { icon: 'receipt', tile: 'amber' },
  no_show_recorded: { icon: 'calendar-x', tile: 'red' },
  card_charge_receipt: { icon: 'receipt', tile: 'green' },
  booking_confirmed: { icon: 'calendar-check', tile: 'green' },
  booking_cancelled: { icon: 'calendar-x', tile: 'red' },
  truck_applied: { icon: 'truck', tile: 'blue' },
  catering_request_new: { icon: 'chef-hat', tile: 'blue' },
  quote_new: { icon: 'chef-hat', tile: 'blue' },
  bid_comment: { icon: 'message-dots', tile: 'blue' },
  message_new: { icon: 'message', tile: 'blue' },
  review_new: { icon: 'star', tile: 'amber' },
  broadcast: { icon: 'speakerphone', tile: 'amber' },
};

// N2's action verbs for the money kinds. The server labels every row; these
// four are the frame's own words, and the no-show row's action goes to the
// help surface, where a charge is disputed. Everything else keeps the
// server's label and link.
const MONEY_ACTIONS = {
  sales_report_submitted: { label: 'Edit report' },
  sales_report_locked: { label: 'View report' },
  cancellation_settled: { label: 'View charge' },
  no_show_recorded: { label: 'Dispute', url: (truckSlug) => `/vendor/truck/${truckSlug}/help` },
};

export function actionFor(row, truckSlug = null) {
  const override = MONEY_ACTIONS[row.kind];
  const url = override && override.url && truckSlug ? override.url(truckSlug) : row.url;
  const label = (override && override.label) || row.action_label;
  return url && label ? { url, label } : null;
}

// Rows from before the spine carry no kind — fall back on what the
// notifiable was. Only the three types the legacy producers ever wrote.
const TYPE_VISUALS = {
  Message: { icon: 'speakerphone', tile: 'amber' },
  ConversationMessage: { icon: 'message', tile: 'blue' },
  Comment: { icon: 'message-dots', tile: 'blue' },
};

export function visualFor(row) {
  return KIND_VISUALS[row.kind]
    || TYPE_VISUALS[row.notifiable_type]
    || { icon: 'bell', tile: 'grey' };
}

// The page's filter pills — Notification::KIND_FILTERS as the city config's
// `notifications.kind_filters` hands them over (installed by
// useNotificationMatrix in notificationMatrix.js); the table below is the
// unreachable fallback. Kinds under no pill (reviews, messages, broadcasts)
// surface only under All.
export function kindFilters() {
  const block = notificationMatrixSource();
  const served = block && block.kind_filters;
  return served && Object.keys(served).length > 0 ? served : FALLBACK_KIND_FILTERS;
}

export const FALLBACK_KIND_FILTERS = {
  bookings: ['booking_confirmed', 'booking_cancelled', 'truck_applied'],
  catering: ['catering_request_new', 'quote_new', 'bid_comment'],
  money: ['payout_sent', 'sales_report_due', 'sales_report_submitted', 'sales_report_locked',
    'cancellation_settled', 'no_show_recorded', 'card_charge_receipt'],
  paperwork: ['document_needs_fix', 'permit_expiring'],
};

export const FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'unread', label: 'Unread' },
  { value: 'bookings', label: 'Bookings' },
  { value: 'catering', label: 'Catering' },
  { value: 'money', label: 'Money' },
  { value: 'paperwork', label: 'Paperwork' },
];

// H2's pills for the host page — the host kinds are applications, quotes,
// cancellations, bookings, messages and broadcasts, and the frame gives
// pills to the first two plus Bookings; the rest surface only under All.
export const HOST_KIND_FILTERS = {
  applications: ['truck_applied'],
  quotes: ['quote_new'],
  bookings: ['booking_confirmed', 'booking_cancelled'],
};

export const HOST_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'unread', label: 'Unread' },
  { value: 'applications', label: 'Applications' },
  { value: 'quotes', label: 'Quotes' },
  { value: 'bookings', label: 'Bookings' },
];

export function matchesFilter(row, filter, filters = kindFilters()) {
  if (filter === 'all') return true;
  if (filter === 'unread') return !row.read_at;
  const kinds = filters[filter];
  return Boolean(kinds && kinds.indexOf(row.kind) !== -1);
}

// Actionable-until-resolved: these render under "Needs you" whatever their
// read state (N2 — read state never moves a row). Mirrors
// Notification::NEEDS_YOU_KINDS.
export const NEEDS_YOU_KINDS = [
  'document_needs_fix',
  'catering_request_new',
  'truck_applied',
  'quote_new',
  'instagram_disconnected',
];

// The phone tray (M1) hides row actions; these kinds keep theirs there
// (owner decision, 29 Sep: the Instagram alert keeps Reconnect on mobile).
export const MOBILE_ACTION_KINDS = ['instagram_disconnected'];

export function needsYou(row) {
  return NEEDS_YOU_KINDS.indexOf(row.kind) !== -1;
}

// The right-rail summary ("Two things need you"): a phrase per kind, joined
// into one sentence.
const NEEDS_YOU_PHRASES = {
  document_needs_fix: 'a paperwork fix',
  catering_request_new: 'a catering request waiting on a quote',
  truck_applied: 'an application to review',
  quote_new: 'a quote to look over',
  instagram_disconnected: 'an Instagram reconnect',
};

// The host page says the same kinds from the host's side — H2's fixture:
// "One application to review and one quote waiting on your answer."
export const HOST_NEEDS_YOU_PHRASES = {
  truck_applied: 'one application to review',
  quote_new: 'one quote waiting on your answer',
};

const COUNT_WORDS = ['Zero', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];

export function needsYouSummary(rows, phrases_ = NEEDS_YOU_PHRASES) {
  const count = rows.length;
  if (count === 0) return null;
  const countWord = count < COUNT_WORDS.length ? COUNT_WORDS[count] : String(count);
  const title = count === 1 ? 'One thing needs you' : `${countWord} things need you`;
  const phrases = rows.slice(0, 3).map((row) => phrases_[row.kind] || 'something to review');
  let body;
  if (phrases.length === 1) {
    body = phrases[0];
  } else if (phrases.length === 2) {
    body = `${phrases[0]} and ${phrases[1]}`;
  } else {
    body = `${phrases.slice(0, -1).join(', ')}, and ${phrases[phrases.length - 1]}`;
  }
  body = body.charAt(0).toUpperCase() + body.slice(1) + '.';
  return { title, body };
}
